import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';

import type { ApiKeyListResponse, ApiKeySummary } from '../../models/api-key.models';
import type {
  CreateWebhookPayload,
  UpdateWebhookPayload,
  WebhookHeaderPayload,
  WebhookSummary,
} from '../../models/webhook.models';
import { ApiKeyApiService } from '../../services/api-key-api.service';
import { WebhookApiService } from '../../services/webhook-api.service';
import { normalizeApiError } from '../../utils/error-normalizer';

type EventOption = {
  key: string;
  label: string;
};

type EventGroup = {
  key: string;
  title: string;
  options: EventOption[];
};

type HeaderDraft = {
  id: string;
  type: 'custom' | 'secret' | 'basic_auth';
  key: string;
  value: string;
};

type ModalMode = 'create' | 'edit';

const EVENT_GROUPS: EventGroup[] = [
  {
    key: 'document',
    title: 'Document Events',
    options: [
      { key: 'document.sent', label: 'Sent' },
      { key: 'document.signed', label: 'Signed' },
      { key: 'document.completed', label: 'Completed' },
      { key: 'document.edited', label: 'Edited' },
      { key: 'document.declined', label: 'Declined' },
      { key: 'document.revoked', label: 'Revoked' },
      { key: 'document.expired', label: 'Expired' },
      { key: 'document.viewed', label: 'Viewed' },
      { key: 'document.signer_saved', label: 'Signer Saved' },
      { key: 'document.authentication_failed', label: 'Authentication Failed' },
      { key: 'document.delivery_failed', label: 'Delivery Failed' },
      { key: 'document.send_failed', label: 'Send Failed' },
      { key: 'document.draft_created', label: 'Draft Created' },
      { key: 'document.reminder', label: 'Reminder' },
      { key: 'document.edit_failed', label: 'Edit Failed' },
    ],
  },
  {
    key: 'sender_identity',
    title: 'Sender Identity Events',
    options: [
      { key: 'sender_identity.created', label: 'Created' },
      { key: 'sender_identity.updated', label: 'Updated' },
      { key: 'sender_identity.deleted', label: 'Deleted' },
      { key: 'sender_identity.revoked', label: 'Revoked' },
      { key: 'sender_identity.verified', label: 'Verified' },
      { key: 'sender_identity.denied', label: 'Denied' },
    ],
  },
  {
    key: 'template',
    title: 'Template Events',
    options: [
      { key: 'template.created', label: 'Created' },
      { key: 'template.deleted', label: 'Deleted' },
      { key: 'template.create_failed', label: 'Create Failed' },
      { key: 'template.edited', label: 'Edited' },
      { key: 'template.draft_created', label: 'Draft Created' },
      { key: 'template.send_failed', label: 'Send Failed' },
    ],
  },
];

const buildEventSelection = (): Record<string, boolean> =>
  EVENT_GROUPS.flatMap((group) => group.options).reduce<Record<string, boolean>>((selection, option) => {
    selection[option.key] = false;
    return selection;
  }, {});

const buildAccordionState = (): Record<string, boolean> =>
  EVENT_GROUPS.reduce<Record<string, boolean>>((state, group, index) => {
    state[group.key] = index === 0;
    return state;
  }, {});

@Component({
  selector: 'app-webhooks',
  imports: [CommonModule, FormsModule],
  templateUrl: './webhooks.component.html',
  styleUrl: './webhooks.component.scss',
})
export class WebhooksComponent implements OnInit {
  readonly eventGroups = EVENT_GROUPS;

  webhooks: WebhookSummary[] = [];
  apiKeyOptions: ApiKeySummary[] = [];
  togglePendingIds = new Set<string>();

  loading = false;
  filterQuery = '';
  errorMessage = '';
  feedbackMessage = '';
  apiKeyLoadError = '';

  modalOpen = false;
  modalMode: ModalMode = 'create';
  modalSubmitting = false;
  modalError = '';
  verificationError = '';
  verificationSuccessMessage = '';
  verificationSubmitting = false;
  verificationToken = '';
  verifiedUrl = '';
  editTargetId: string | null = null;
  originalUrl = '';

  deleteTarget: WebhookSummary | null = null;
  deleteSubmitting = false;

  webhookName = '';
  selectedApiKeyId = '';
  publishUrl = '';
  eventSelection = buildEventSelection();
  accordionOpen = buildAccordionState();
  headerDrafts: HeaderDraft[] = [];

  private headerDraftCounter = 0;

  private readonly webhookApi = inject(WebhookApiService);
  private readonly apiKeyApi = inject(ApiKeyApiService);
  private readonly cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.loadWebhooks();
    this.loadApiKeys();
  }

  get filteredWebhooks(): WebhookSummary[] {
    const query = this.filterQuery.trim().toLowerCase();
    if (!query) {
      return this.webhooks;
    }

    return this.webhooks.filter(
      (webhook) =>
        webhook.name.toLowerCase().includes(query) || webhook.apiKeyName.toLowerCase().includes(query) || webhook.url.toLowerCase().includes(query),
    );
  }

  get paginationLabel(): string {
    const count = this.filteredWebhooks.length;
    return count === 0 ? '0 - 0 of 0' : `1 - ${count} of ${count}`;
  }

  get hasSelectedEvents(): boolean {
    return this.selectedEventKeys.length > 0;
  }

  get selectedEventKeys(): string[] {
    return Object.entries(this.eventSelection)
      .filter(([, selected]) => selected)
      .map(([key]) => key);
  }

  get allEventsSelected(): boolean {
    return this.selectedEventKeys.length === EVENT_GROUPS.flatMap((group) => group.options).length;
  }

  get canVerifyWebhook(): boolean {
    return !this.verificationSubmitting && this.publishUrl.trim().length > 0;
  }

  get hasBasicAuthHeader(): boolean {
    return this.headerDrafts.some((header) => header.type === 'basic_auth');
  }

  get areHeadersValid(): boolean {
    return this.headerDrafts.every((header) => header.key.trim().length > 0 && header.value.trim().length > 0);
  }

  get isEditing(): boolean {
    return this.modalMode === 'edit';
  }

  get currentModalTitle(): string {
    return this.isEditing ? 'Edit Webhook' : 'Add Webhook';
  }

  get submitButtonLabel(): string {
    if (this.modalSubmitting) {
      return this.isEditing ? 'Saving…' : 'Saving…';
    }

    return this.isEditing ? 'Save' : 'Save';
  }

  get requiresVerification(): boolean {
    return !this.isEditing || this.publishUrl.trim() !== this.originalUrl.trim();
  }

  get canSaveWebhook(): boolean {
    return Boolean(
      !this.modalSubmitting &&
        this.webhookName.trim() &&
        this.selectedApiKeyId &&
        this.publishUrl.trim() &&
        this.hasSelectedEvents &&
        this.areHeadersValid &&
        (!this.requiresVerification || this.verificationToken),
    );
  }

  openAddModal(): void {
    this.feedbackMessage = '';
    this.modalMode = 'create';
    this.modalOpen = true;
    this.editTargetId = null;
    this.originalUrl = '';
    this.resetModalForm();
  }

  openEditModal(webhook: WebhookSummary): void {
    this.feedbackMessage = '';
    this.modalMode = 'edit';
    this.modalOpen = true;
    this.editTargetId = webhook.id;
    this.originalUrl = webhook.url;
    this.modalError = '';
    this.verificationError = '';
    this.verificationSuccessMessage = '';
    this.verificationToken = '';
    this.verifiedUrl = webhook.url;
    this.webhookName = webhook.name;
    this.selectedApiKeyId = webhook.apiKeyId;
    this.publishUrl = webhook.url;
    this.eventSelection = buildEventSelection();
    webhook.eventKeys.forEach((eventKey) => {
      if (eventKey in this.eventSelection) {
        this.eventSelection[eventKey] = true;
      }
    });
    this.accordionOpen = buildAccordionState();
    this.headerDrafts = webhook.headers.map((header) => this.toHeaderDraft(header));
    this.headerDraftCounter = this.headerDrafts.length;
  }

  closeModal(force = false): void {
    if ((this.modalSubmitting || this.verificationSubmitting) && !force) {
      return;
    }

    this.modalOpen = false;
    this.editTargetId = null;
    this.originalUrl = '';
    this.resetModalForm();
  }

  openDeleteConfirm(webhook: WebhookSummary): void {
    this.feedbackMessage = '';
    this.deleteTarget = webhook;
  }

  closeDeleteConfirm(force = false): void {
    if (this.deleteSubmitting && !force) {
      return;
    }

    this.deleteTarget = null;
  }

  setPublishUrl(value: string): void {
    if (value !== this.publishUrl) {
      this.clearVerificationState();
    }

    this.publishUrl = value;

    if (this.isEditing && value.trim() === this.originalUrl.trim()) {
      this.verificationSuccessMessage = '';
      this.verificationError = '';
    }
  }

  verifyPublishUrl(): void {
    if (!this.canVerifyWebhook) {
      return;
    }

    this.verificationSubmitting = true;
    this.verificationError = '';
    this.verificationSuccessMessage = '';

    this.webhookApi
      .verifyWebhookUrl({ url: this.publishUrl.trim() })
      .pipe(
        finalize(() => {
          this.verificationSubmitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.verificationToken = response.verificationToken;
          this.verifiedUrl = response.verifiedUrl;
          this.verificationSuccessMessage = 'Webhook URL verified successfully.';
          this.publishUrl = response.verifiedUrl;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.verificationError = normalizeApiError(error, {
            fallbackMessage: 'Unable to verify webhook URL.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  submitWebhook(): void {
    if (!this.canSaveWebhook) {
      return;
    }

    this.modalSubmitting = true;
    this.modalError = '';

    const basePayload = {
      name: this.webhookName.trim().replace(/\s+/g, ' '),
      apiKeyId: this.selectedApiKeyId,
      url: this.publishUrl.trim(),
      eventKeys: this.selectedEventKeys,
      headers: this.toHeaderPayloads(),
    };

    if (this.isEditing && this.editTargetId) {
      const payload: UpdateWebhookPayload = {
        ...basePayload,
        verificationToken: this.requiresVerification ? this.verificationToken : undefined,
      };

      this.webhookApi
        .updateWebhook(this.editTargetId, payload)
        .pipe(
          finalize(() => {
            this.modalSubmitting = false;
            this.cdr.markForCheck();
          }),
        )
        .subscribe({
          next: (updatedWebhook) => {
            this.webhooks = this.webhooks.map((webhook) => (webhook.id === updatedWebhook.id ? updatedWebhook : webhook));
            this.feedbackMessage = `Webhook ${updatedWebhook.name} updated successfully.`;
            this.closeModal(true);
            this.cdr.markForCheck();
          },
          error: (error: unknown) => {
            this.modalError = normalizeApiError(error, {
              fallbackMessage: 'Unable to update webhook.',
            }).message;
            this.cdr.markForCheck();
          },
        });

      return;
    }

    const payload: CreateWebhookPayload = {
      ...basePayload,
      verificationToken: this.verificationToken,
    };

    this.webhookApi
      .createWebhook(payload)
      .pipe(
        finalize(() => {
          this.modalSubmitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (createdWebhook) => {
          this.webhooks = [createdWebhook, ...this.webhooks];
          this.feedbackMessage = `Webhook ${createdWebhook.name} created successfully.`;
          this.closeModal(true);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.modalError = normalizeApiError(error, {
            fallbackMessage: 'Unable to create webhook.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  confirmDelete(): void {
    const target = this.deleteTarget;
    if (!target || this.deleteSubmitting) {
      return;
    }

    this.deleteSubmitting = true;
    this.errorMessage = '';

    this.webhookApi
      .deleteWebhook(target.id)
      .pipe(
        finalize(() => {
          this.deleteSubmitting = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.webhooks = this.webhooks.filter((webhook) => webhook.id !== target.id);
          this.feedbackMessage = `Webhook ${target.name} deleted successfully.`;
          this.closeDeleteConfirm(true);
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to delete webhook.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  toggleWebhookState(webhook: WebhookSummary, active: boolean): void {
    this.togglePendingIds = new Set(this.togglePendingIds).add(webhook.id);
    this.errorMessage = '';

    this.webhookApi
      .updateWebhookState(webhook.id, { active })
      .pipe(
        finalize(() => {
          const nextPending = new Set(this.togglePendingIds);
          nextPending.delete(webhook.id);
          this.togglePendingIds = nextPending;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (updatedWebhook) => {
          this.webhooks = this.webhooks.map((row) => (row.id === updatedWebhook.id ? updatedWebhook : row));
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to update webhook state.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  toggleAllEvents(selected: boolean): void {
    this.eventSelection = Object.keys(this.eventSelection).reduce<Record<string, boolean>>((next, key) => {
      next[key] = selected;
      return next;
    }, {});
  }

  toggleGroupAll(group: EventGroup, selected: boolean): void {
    const nextSelection = { ...this.eventSelection };
    group.options.forEach((option) => {
      nextSelection[option.key] = selected;
    });
    this.eventSelection = nextSelection;
  }

  setEventSelection(eventKey: string, selected: boolean): void {
    this.eventSelection = {
      ...this.eventSelection,
      [eventKey]: selected,
    };
  }

  areGroupEventsSelected(group: EventGroup): boolean {
    return group.options.every((option) => this.eventSelection[option.key]);
  }

  selectedGroupCount(group: EventGroup): number {
    return group.options.filter((option) => this.eventSelection[option.key]).length;
  }

  toggleAccordion(groupKey: string): void {
    this.accordionOpen = {
      ...this.accordionOpen,
      [groupKey]: !this.accordionOpen[groupKey],
    };
  }

  addHeader(type: HeaderDraft['type']): void {
    if (type === 'basic_auth' && this.hasBasicAuthHeader) {
      return;
    }

    this.headerDraftCounter += 1;
    this.headerDrafts = [
      ...this.headerDrafts,
      {
        id: `header_${this.headerDraftCounter}`,
        type,
        key: '',
        value: '',
      },
    ];
  }

  removeHeader(headerId: string): void {
    this.headerDrafts = this.headerDrafts.filter((header) => header.id !== headerId);
  }

  trackByWebhook(_: number, webhook: WebhookSummary): string {
    return webhook.id;
  }

  trackByHeader(_: number, header: HeaderDraft): string {
    return header.id;
  }

  formatEnvironment(environment: ApiKeySummary['environment']): string {
    return environment === 'sandbox' ? 'Sandbox' : 'Live';
  }

  private toHeaderDraft(header: WebhookHeaderPayload): HeaderDraft {
    this.headerDraftCounter += 1;

    return {
      id: `header_${this.headerDraftCounter}`,
      type: header.type,
      key: header.type === 'basic_auth' ? header.username : header.key,
      value: header.type === 'basic_auth' ? header.password : header.value,
    };
  }

  private toHeaderPayloads(): WebhookHeaderPayload[] {
    return this.headerDrafts.map((header) =>
      header.type === 'basic_auth'
        ? {
            type: 'basic_auth',
            username: header.key.trim(),
            password: header.value.trim(),
          }
        : {
            type: header.type,
            key: header.key.trim(),
            value: header.value.trim(),
          },
    );
  }

  private loadWebhooks(): void {
    this.loading = true;
    this.errorMessage = '';

    this.webhookApi
      .listWebhooks()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.webhooks = response.items;
          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to load webhooks.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  private loadApiKeys(): void {
    this.apiKeyLoadError = '';

    this.apiKeyApi.listApiKeys().subscribe({
      next: (response: ApiKeyListResponse) => {
        this.apiKeyOptions = response.items;
        this.cdr.markForCheck();
      },
      error: (error: unknown) => {
        this.apiKeyLoadError = normalizeApiError(error, {
          fallbackMessage: 'Unable to load API keys for webhook creation.',
        }).message;
        this.cdr.markForCheck();
      },
    });
  }

  private clearVerificationState(): void {
    this.verificationToken = '';
    this.verifiedUrl = '';
    this.verificationError = '';
    this.verificationSuccessMessage = '';
  }

  private resetModalForm(): void {
    this.modalError = '';
    this.webhookName = '';
    this.selectedApiKeyId = '';
    this.publishUrl = '';
    this.eventSelection = buildEventSelection();
    this.accordionOpen = buildAccordionState();
    this.headerDrafts = [];
    this.headerDraftCounter = 0;
    this.clearVerificationState();
  }
}
